import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import Loading from "react-fullscreen-loading";
import { FiMail } from "react-icons/fi";
import UserDashboardLayout from "../../../Components/UserDashboardLayout/UserDashboardLayout";
import { useAuth } from "../../../authentication/AuthContext";
import "./VerifyEmail.css";

// Codes are 5 digits (see insertVerificationCode in the backend).
const CODE_LENGTH = 6;
// Matches the backend's resend throttle window.
const RESEND_COOLDOWN = 300;

// Confirming the email address is a portal page, not an auth screen: signup
// already signed the user in, so they reach this from the dashboard banner with
// the navbar and sidebar around them. Showing them a signed-out-looking split
// screen at this point would read as being logged out.
export default function VerifyEmail() {
  const navigate = useNavigate();
  const { status, verified, email, setSession, markVerified } = useAuth();
  const [userCode, setUserCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [timer, setTimer] = useState(0);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer(prev => prev - 1), 1000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [timer]);

  // Nothing to do here once the address is confirmed — including when it gets
  // confirmed in another tab and verifytoken notices.
  useEffect(() => {
    if (status === "ready" && verified) {
      navigate("/User/Dashboard", { replace: true });
    }
  }, [status, verified, navigate]);

  // Digits only, capped at the code length — but never submitted for them.
  // Typing the last digit is not a decision to submit: a mistyped digit would
  // burn an attempt before the user has had a chance to read what they typed.
  const handleCodeChange = (e) => {
    setUserCode(e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    if (userCode.length !== CODE_LENGTH) {
      toast.error(`Please enter the ${CODE_LENGTH}-digit code from your email.`);
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/auth/verifyemail`, {
        email: email,
        usercode: userCode,
      });

      if (res.data?.status === 1) {
        // The endpoint reissues tokens, so take them — this both clears the
        // banner and refreshes the session in one step.
        const accessToken = res.headers["access-token"] || res.data.data?.accessToken;
        const refreshToken = res.headers["refresh-token"] || res.data.data?.refreshToken;

        if (accessToken && refreshToken) {
          localStorage.setItem("accessToken", accessToken);
          localStorage.setItem("refreshToken", refreshToken);
          localStorage.setItem("userType", res.data.userType);
          localStorage.setItem("email", email);
          if (res.data.quote_requested_at) {
            localStorage.setItem("quote_requested_at", res.data.quote_requested_at);
          }
          setSession({
            userType: res.data.userType,
            role: res.data.role,
            roleName: res.data.roleName,
            permissions: res.data.permissions,
            verified: true,
            profileComplete: res.data.profileComplete,
            email,
          });
        } else {
          markVerified();
        }

        toast.success("Email verified — thanks!");
        navigate("/User/Dashboard", { replace: true });
        return;
      }

      setUserCode("");
      toast.error(res.data?.message || "Could not verify that code.");
      if (res.data?.message === "Code expired.") {
        setTimer(0);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to connect to the server. Please try again later.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const resend = async () => {
    if (resending || timer > 0) return;
    setResending(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/auth/ResendVerificationCode`, { email });
      if (res.data?.status === 1) {
        toast.success("Code sent — check your inbox.");
        setTimer(RESEND_COOLDOWN);
      } else if (res.data?.status === 2) {
        // Already confirmed elsewhere; the effect above takes it from here.
        toast.success(res.data.message);
        markVerified();
      } else {
        toast.error(res.data?.message || "Could not send the code. Please try again.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to connect to the server. Please try again later.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div>
      <UserDashboardLayout />
      <Loading loading={submitting || resending} background="#fff" loaderColor="#883484" />
      <div className="content-page-bg">
        <div className="content-page">
          <div className="content">
            <div className="verify-page">
              <div className="verify-card">
                <div className="verify-card-icon" aria-hidden="true">
                  <FiMail />
                </div>
                <h1>Confirm your email</h1>
                <p className="verify-card-sub">
                  Enter the {CODE_LENGTH}-digit code we sent to <strong>{email}</strong>.
                </p>

                <form onSubmit={submit}>
                  <label className="verify-label" htmlFor="verificationCode">
                    Verification code
                  </label>
                  <input
                    id="verificationCode"
                    className="verify-code-input"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    name="one-time-code"
                    placeholder="123456"
                    maxLength={CODE_LENGTH}
                    autoFocus
                    value={userCode}
                    onChange={handleCodeChange}
                  />
                  <button type="submit" className="verify-submit" disabled={submitting}>
                    {submitting ? "Verifying…" : "Verify email"}
                  </button>
                </form>

                <p className="verify-footnote">
                  Didn't receive it?{" "}
                  {timer > 0 ? (
                    `Send again in ${Math.floor(timer / 60)}:${timer % 60 < 10 ? "0" : ""}${timer % 60}`
                  ) : (
                    <button type="button" className="verify-link" onClick={resend} disabled={resending}>
                      {resending ? "Sending…" : "Send a new code"}
                    </button>
                  )}
                </p>
                <p className="verify-secondary">
                  <button
                    type="button"
                    className="verify-link"
                    onClick={() => navigate("/User/Dashboard")}
                  >
                    Skip for now
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
